import { type FC, type ReactNode, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { SelectDropdown, type SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { type EmailTemplatePurpose, type SchedulingEventType } from '../../../../../models/Scheduling'
import { TemplatePreviewModal } from '../TemplatePreviewModal'
import { useSchedulingEmailTemplates } from '../../../../Scheduling/shared/useSchedulingEmailTemplates'
import { AUTOMATIC_EMAIL_SLOTS, USER_EMAIL_SLOTS } from './emailTemplates'

interface EmailTemplatesTabProps {
  draft: SchedulingEventType
  disabled: boolean
  onDraftChange: (patch: Partial<SchedulingEventType>) => void
}

const TemplateRow: FC<{ label: string; helper: string; children: ReactNode }> = ({ label, helper, children }) => (
  <Row className='align-items-center gy-2'>
    <Col md={5}>
      <div className='bold'>{label}</div>
      <div className='paragraph-small zocks-text-gray-600'>{helper}</div>
    </Col>
    <Col md={7}>{children}</Col>
  </Row>
)

export const EmailTemplatesTab: FC<EmailTemplatesTabProps> = ({ draft, disabled, onDraftChange }) => {
  const [previewTemplateId, setPreviewTemplateId] = useState<string | undefined>(undefined)
  const { findById, forPurpose } = useSchedulingEmailTemplates()

  const templateIdFor = (purpose: EmailTemplatePurpose): string | undefined =>
    draft.emailTemplates.find((assignment) => assignment.purpose === purpose)?.emailTemplateId

  const setTemplate = (purpose: EmailTemplatePurpose, emailTemplateId?: string) => {
    const rest = draft.emailTemplates.filter((assignment) => assignment.purpose !== purpose)
    onDraftChange({ emailTemplates: emailTemplateId ? [...rest, { purpose, emailTemplateId }] : rest })
  }

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='d-flex flex-column gap-3'>
        <div>
          <div className='lead-small zocks-text-gray-500 text-uppercase'>Automatically sent</div>
          <div className='paragraph-small zocks-text-gray-600'>These send automatically using a standard template. No review needed.</div>
        </div>
        {AUTOMATIC_EMAIL_SLOTS.map((slot) => {
          const templateId = templateIdFor(slot.purpose)
          return (
            <TemplateRow key={slot.purpose} label={slot.label} helper={slot.helper}>
              <div className='d-flex align-items-center justify-content-between gap-2 border rounded-2 zocks-border-color-gray-300 zocks-px-1.25 py-2'>
                <span className='d-flex align-items-center zocks-gap-0.5 text-truncate'>
                  <i className='bi bi-file-earmark-text zocks-text-gray-600' />
                  <span className='text-truncate'>{findById(templateId)?.name ?? 'Standard template'}</span>
                </span>
                <Button
                  variant='grayGhost'
                  className='p-1 flex-shrink-0'
                  aria-label={`Preview ${slot.label} template`}
                  onClick={() => setPreviewTemplateId(templateId)}
                >
                  <i className='bi bi-pencil' />
                </Button>
              </div>
            </TemplateRow>
          )
        })}
      </div>
      <div className='border-top zocks-border-color-gray-200' />
      <div className='d-flex flex-column gap-3'>
        <div>
          <div className='lead-small zocks-text-gray-500 text-uppercase'>User sent</div>
          <div className='paragraph-small zocks-text-gray-600'>A user reviews these before they send.</div>
        </div>
        {USER_EMAIL_SLOTS.map((slot) => {
          const templateId = templateIdFor(slot.purpose)
          const options: SelectOption[] = forPurpose(slot.purpose).map((template) => ({ id: template.id, label: template.name }))
          const selected = options.find((option) => option.id === templateId) ?? null
          return (
            <TemplateRow key={slot.purpose} label={slot.label} helper={slot.helper}>
              <SelectDropdown
                options={options}
                initialSelectedOption={selected}
                placeholder='Choose a template'
                disabled={disabled}
                inputAutoComplete='off'
                showDeselectOption
                deselectOptionLabel='None'
                onOptionSelected={(option) => setTemplate(slot.purpose, option.id)}
                onDeselect={() => setTemplate(slot.purpose, undefined)}
              />
            </TemplateRow>
          )
        })}
      </div>
      {previewTemplateId ? <TemplatePreviewModal template={findById(previewTemplateId)} onClose={() => setPreviewTemplateId(undefined)} /> : null}
    </div>
  )
}
